import { IWidgetData, WidgetPlaceType, IWidgetVisibility, IVisibility } from "@pageBuilder/typings";
import { RendererBase } from "../../shared/renderer-base";
import { WidgetAnimationRenderer } from "./widget-animation.renderer";
import { ElementUtil } from "src/utils/element-util";
import { IRenderingCommonContext } from "@commonRendering/types";

export abstract class WidgetRendererBase<TData extends IWidgetData> extends RendererBase<TData> {

    protected readonly animationRenderer: WidgetAnimationRenderer;

    constructor(renderingCommonContext: IRenderingCommonContext) {
        super(renderingCommonContext);
        this.animationRenderer = new WidgetAnimationRenderer(this.styleService);
    }

    public override render(element: HTMLElement, data: TData): HTMLElement {
        this.applyWidgetVisibility(element, data);
        this.applyAttributes(element, data);
        this.applyClasses(element, data);

        this.renderedDocument.body.classList.add("render-in-progress");

        const renderElement = super.render(element, data);
        if (!this.isEditorRendering) {
            this.setEmptyContentClassIfNeeded(element);
        }

        this.renderedDocument.body.classList.remove("render-in-progress");

        return renderElement;
    }

    private setEmptyContentClassIfNeeded(widget: HTMLElement): void {
        widget.classList.toggle("empty-content", this.isEmpty(widget));
    }

    protected isEmpty(widget: HTMLElement): boolean {
        const widgetType = ElementUtil.resolveElementType(widget);
        if (ElementUtil.widgetsWithDefaultContent.includes(widgetType)) {
            return false;
        }

        const content = widget.querySelector<HTMLElement>(":scope > .widget-content, :scope > .widget-contents");
        if (!content) {
            return true;
        }

        const notAvailable = content.querySelector<HTMLElement>(".not-available");
        if (notAvailable) {
            return true;
        }

        const ckContent = content.querySelector<HTMLElement>(".ck-content");
        return (content.childNodes.length === 0 || ckContent?.childNodes.length === 0)
         && !ElementUtil.widgetsWithEmptyContent.includes(widgetType);
    }

    public override rerender(element: HTMLElement, data: TData): HTMLElement {
        this.applyWidgetVisibility(element, data);
        return super.rerender(element, data);
    }

    protected createWidgetElement(dataWidget: string, existContent?: HTMLElement): HTMLElement {
        const element = document.createElement("div");
        const content = existContent ?? this.createContentElement();
        element.append(content);
        element.dataset.widget = dataWidget;

        return element;
    }

    protected createContentElement(): HTMLElement {
        const content = document.createElement("div");
        content.classList.add("widget-content");
        return content;
    }

    protected getWidgetVisibility(element: HTMLElement): IVisibility {
        const hideInDesktop = element.classList.contains("hide-in-desktop");
        const hideInTablet = element.classList.contains("hide-in-tablet");
        const hideInMobile = element.classList.contains("hide-in-mobile");

        return {
            desktop: !hideInDesktop,
            tablet: !hideInTablet,
            mobile: !hideInMobile
        };
    }

    protected executeScripts(element: HTMLElement): void {
        const scripts = element.querySelectorAll("script");
        scripts.forEach(oldScript => {
            const newScript = document.createElement("script");
            Array.from(oldScript.attributes).forEach(attr => newScript.setAttribute(attr.name, attr.value));
            newScript.textContent = oldScript.textContent;
            oldScript.parentNode?.replaceChild(newScript, oldScript);
        });
    }

    private applyWidgetVisibility(element: HTMLElement, widgetData: TData): void {
        const widgetVisibility = (widgetData as IWidgetVisibility).visibility;

        element.classList.remove("hide-in-desktop", "hide-in-tablet", "hide-in-mobile");

        if (widgetVisibility && !widgetVisibility.desktop){
            element.classList.add("hide-in-desktop");
        }
        if (widgetVisibility && !widgetVisibility.tablet){
            element.classList.add("hide-in-tablet");
        }
        if (widgetVisibility && !widgetVisibility.mobile){
            element.classList.add("hide-in-mobile");
        }
    }

    protected getPlaceType(element: HTMLElement): WidgetPlaceType {
        if (!element.hasAttribute("data-place-type")) {
            return "Section";
        }

        return element.dataset.placeType as WidgetPlaceType ?? "Section";
    }

    private applyAttributes(element: HTMLElement, data: TData): void {
        if (element.hasAttribute("data-widget")) {
            const widgetData = data as IWidgetData;
            element.dataset.placeType = widgetData.placeType;
        }
    }

    private applyClasses(element: HTMLElement, data: TData): void {
        if (element.hasAttribute("data-widget")) {
            const widgetData = data as IWidgetData;
            if (widgetData.placeType === "BoxWidget") {
                element.classList.add("boxed-widget");
            }
        }
    }

    protected abstract renderStyles(element: HTMLElement, data: TData): void

}
